/** Utilidades de DOM: creación de elementos, popovers anclados, modales y avisos. */

type Attrs = Record<string, string | number | boolean | ((e: Event) => void) | undefined>

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag)
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === false) continue
    if (key === 'class') el.className = String(value)
    else if (key === 'html') el.innerHTML = String(value)
    else if (key.startsWith('on') && typeof value === 'function') {
      el.addEventListener(key.slice(2).toLowerCase(), value as EventListener)
    } else if (value === true) el.setAttribute(key, '')
    else el.setAttribute(key, String(value))
  }
  for (const child of children) el.append(child)
  return el
}

export function clear(el: HTMLElement): void {
  while (el.firstChild) el.firstChild.remove()
}

/* ── Popovers anclados ───────────────────────────────────────── */

let openPopover: { el: HTMLElement; anchor: HTMLElement | null; onClose?: () => void } | null = null

export function closePopover(): void {
  if (!openPopover) return
  openPopover.el.remove()
  openPopover.onClose?.()
  openPopover = null
}

export function isPopoverOpenFor(anchor: HTMLElement): boolean {
  return openPopover?.anchor === anchor
}

export interface PopoverOptions {
  anchor: HTMLElement
  /** 'top' coloca el panel encima del ancla (barra inferior) */
  placement?: 'top' | 'bottom'
  align?: 'start' | 'center' | 'end'
  onClose?: () => void
}

export function openPopoverAt(content: HTMLElement, options: PopoverOptions): HTMLElement {
  closePopover()
  content.classList.add('flyout', 'surface')
  document.body.append(content)
  const rect = options.anchor.getBoundingClientRect()
  const size = content.getBoundingClientRect()
  const gap = 8
  const placement = options.placement ?? 'top'
  let top = placement === 'top' ? rect.top - size.height - gap : rect.bottom + gap
  const align = options.align ?? 'center'
  let left =
    align === 'start'
      ? rect.left
      : align === 'end'
        ? rect.right - size.width
        : rect.left + rect.width / 2 - size.width / 2
  left = Math.max(8, Math.min(left, window.innerWidth - size.width - 8))
  top = Math.max(8, Math.min(top, window.innerHeight - size.height - 8))
  content.style.left = `${Math.round(left)}px`
  content.style.top = `${Math.round(top)}px`
  openPopover = { el: content, anchor: options.anchor, onClose: options.onClose }
  return content
}

export function openMenuAtPoint(content: HTMLElement, clientX: number, clientY: number): HTMLElement {
  closePopover()
  content.classList.add('flyout', 'surface')
  document.body.append(content)
  const size = content.getBoundingClientRect()
  const left = Math.min(clientX, window.innerWidth - size.width - 8)
  const top = Math.min(clientY, window.innerHeight - size.height - 8)
  content.style.left = `${Math.round(Math.max(8, left))}px`
  content.style.top = `${Math.round(Math.max(8, top))}px`
  openPopover = { el: content, anchor: null }
  return content
}

document.addEventListener('pointerdown', (e) => {
  if (!openPopover) return
  const target = e.target as Node
  if (openPopover.el.contains(target)) return
  if (openPopover.anchor?.contains(target)) return
  closePopover()
})

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && openPopover) closePopover()
})

/* ── Modales ─────────────────────────────────────────────────── */

export function openDialog(build: (close: () => void) => HTMLElement): () => void {
  const scrim = h('div', { class: 'scrim' })
  const close = (): void => scrim.remove()
  const dialog = build(close)
  dialog.classList.add('dialog')
  dialog.addEventListener('pointerdown', (e) => e.stopPropagation())
  scrim.append(dialog)
  scrim.addEventListener('pointerdown', () => close())
  document.body.append(scrim)
  const focusable = dialog.querySelector<HTMLElement>('input, button, textarea')
  focusable?.focus()
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      close()
      window.removeEventListener('keydown', onKey)
    }
  }
  window.addEventListener('keydown', onKey)
  return close
}

/* ── Avisos ──────────────────────────────────────────────────── */

let toastHost: HTMLElement | null = null

export function toast(message: string, duration = 2600): void {
  if (!toastHost) {
    toastHost = h('div', { class: 'toast-host' })
    document.body.append(toastHost)
  }
  const el = h('div', { class: 'toast' }, [message])
  toastHost.append(el)
  setTimeout(() => {
    el.style.transition = 'opacity .2s ease'
    el.style.opacity = '0'
    setTimeout(() => el.remove(), 220)
  }, duration)
}

/** Botón con icono para las barras. */
export function toolButton(options: {
  icon: string
  title: string
  onClick: (e: MouseEvent) => void
  active?: boolean
  disabled?: boolean
  className?: string
}): HTMLButtonElement {
  const btn = h('button', {
    class: `tbtn ${options.className ?? ''} ${options.active ? 'is-active' : ''}`.trim(),
    title: options.title,
    'aria-label': options.title,
    html: options.icon,
    disabled: options.disabled,
    onclick: options.onClick as (e: Event) => void,
  })
  return btn
}
